import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, FileText, Check, AlertCircle } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { OwnerService } from '../../handling_data/services/OwnerService';
import { Owner } from '../../handling_data/models/Owner';

interface OwnerFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    owner?: Owner | null;
}

export const OwnerFormModal: React.FC<OwnerFormModalProps> = ({ isOpen, onClose, onSuccess, owner }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');

    const [certificateFiles, setCertificateFiles] = useState<File[]>([]);
    const [existingCertificates, setExistingCertificates] = useState<{ id: string, url: string }[]>([]);
    const [deletedCertificates, setDeletedCertificates] = useState<string[]>([]);

    const certificatesInputRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (owner) {
            setName(owner.profile?.official_name || owner.name || '');
            setEmail(owner.email || '');
            setPhone(owner.phone || owner.profile?.contact_phone || '');
            setPassword('');
            setDeletedCertificates([]);

            // Populate existing certificates
            const certs = owner.profile?.certificates || owner.profile?.certificates_urls;
            if (certs) {
                if (Array.isArray(certs)) {
                    setExistingCertificates(certs.map((url, index) => ({ id: index.toString(), url })));
                } else if (typeof certs === 'object') {
                    setExistingCertificates(Object.entries(certs as any).map(([id, url]) => ({ id, url: url as string })));
                } else {
                    setExistingCertificates([]);
                }
            } else {
                setExistingCertificates([]);
            }
        } else {
            resetForm();
        }
    }, [owner, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('email', email);
            formData.append('contact_phone', phone);
            if (password) formData.append('password', password);

            // New files
            certificateFiles.forEach((file) => {
                formData.append('certificates[]', file);
            });

            let response;
            if (owner) {
                // Update mode
                if (deletedCertificates.length > 0) {
                    deletedCertificates.forEach(path => {
                        formData.append('deleted_certificates[]', path);
                    });
                }
                response = await OwnerService.updateOwner(owner.id, formData);
            } else {
                // Create mode
                response = await OwnerService.createOwner(formData);
            }

            if (response.status === 'success' || response.status === 'created' || response.status === '201') {
                resetForm();
                onSuccess();
                onClose();
            } else {
                setError(response.message || 'فشل في العملية');
            }
        } catch (err: any) {
            console.error('Owner save error:', err);

            if (err.response?.status === 422) {
                const data = err.response.data;
                const errors = data.errors;

                if (errors) {
                    const allMessages = Object.values(errors).flat() as string[];
                    setError(allMessages.join(' | '));
                } else {
                    setError(data.message || 'حدث خطأ في التحقق من البيانات');
                }
            } else {
                setError(err.response?.data?.message || err.message || 'حدث خطأ غير متوقع');
            }
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setEmail('');
        setPassword('');
        setPhone('');
        setCertificateFiles([]);
        setExistingCertificates([]);
        setDeletedCertificates([]);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={owner ? `تعديل المالك: ${owner.profile?.official_name || owner.name}` : t('owners_page.add_new')}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t('login.name') || 'الاسم'}</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('login.name_placeholder') || 'أدخل الاسم الكامل'}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t('login.email')}</label>
                        <input
                            type="email"
                            required
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@test.com"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">
                            {t('login.password')}
                            {owner && <span className="text-[10px] text-gray-400 mr-2">(اختياري)</span>}
                        </label>
                        <input
                            type="password"
                            required={!owner}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="********"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-gray-700">{t('owners_page.table.phone')}</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="05xxxxxxx"
                        />
                    </div>
                </div>

                {/* File Uploads */}
                <div className="space-y-4 pt-2">
                    {/* Existing Certificates */}
                    {existingCertificates.length > 0 && (
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">{t('owners_page.existing_certificates')}</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {existingCertificates.map((cert: { id: string, url: string }) => {
                                    const isPdf = cert.url.toLowerCase().endsWith('.pdf');
                                    const fullUrl = cert.url.startsWith('http') ? cert.url : `https://api.istanbulviillas.com/storage/${cert.url}`;

                                    return (
                                        <div key={cert.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 text-gray-700 rounded-lg border border-gray-200 text-[11px] font-medium group">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <FileText className="w-3.5 h-3.5 text-primary/60" />
                                                <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="truncate hover:text-primary transition-colors">
                                                    {cert.url.split('/').pop()}
                                                </a>
                                            </div>
                                            <button
                                                type="button"
                                                disabled={loading}
                                                onClick={() => {
                                                    if (window.confirm(t('owners_page.confirm_delete_certificate_msg'))) {
                                                        // Deferred Delete: Add to deleted list and remove from UI
                                                        setDeletedCertificates((prev: string[]) => [...prev, cert.url]);
                                                        setExistingCertificates((prev: any[]) => prev.filter((c: any) => c.id !== cert.id));
                                                    }
                                                }}
                                                className="p-1 hover:text-red-500 transition-colors"
                                                title={t('common.delete')}
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">{t('common.certificates')} (صور أو ملفات PDF)</label>
                        <div
                            onClick={() => certificatesInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary/40 hover:bg-gray-50 transition-all"
                        >
                            <input
                                type="file"
                                multiple
                                accept=".jpg,.jpeg,.png,.pdf"
                                ref={certificatesInputRef}
                                className="hidden"
                                onChange={(e) => {
                                    if (e.target.files) {
                                        const files = Array.from(e.target.files);
                                        const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
                                        const maxSize = 5 * 1024 * 1024; // 5MB

                                        const filteredFiles = files.filter(file => {
                                            if (!validTypes.includes(file.type)) {
                                                setError(`${t('common.error')}: ${file.name} - نوع الملف غير مدعوم (JPG, PNG, PDF فقط)`);
                                                return false;
                                            }
                                            if (file.size > maxSize) {
                                                setError(`${t('common.error')}: ${file.name} - حجم الملف كبير جداً (الحد الأقصى 5 ميجا)`);
                                                return false;
                                            }
                                            return true;
                                        });

                                        setCertificateFiles(prev => [...prev, ...filteredFiles]);
                                    }
                                }}
                            />
                            <div className="w-12 h-12 rounded-full bg-primary/5 flex items-center justify-center text-primary">
                                <Upload className="w-6 h-6" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-gray-700">{t('property_form.click_to_upload')}</p>
                                <p className="text-xs text-gray-500 mt-1">يمكنك اختيار ملفات متعددة (JPG, PNG, PDF - بحد أقصى 5 ميجا)</p>
                            </div>
                        </div>

                        {certificateFiles.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                {certificateFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between px-3 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 text-[11px] font-medium group">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                                            <span className="truncate">{file.name}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCertificateFiles(prev => prev.filter((_, i) => i !== idx));
                                            }}
                                            className="p-1 hover:text-red-500 transition-colors"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                    <Button
                        type="button"
                        onClick={onClose}
                        className="px-6"
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        disabled={loading}
                        className="bg-primary text-white px-8 shadow-lg shadow-primary/20"
                    >
                        {loading ? (
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                <span>{t('common.loading')}...</span>
                            </div>
                        ) : t('common.save')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
